import { createContext, useContext, useState, useEffect } from 'react';
import { subscribeLobby } from '../firebase/services';

// Create game context
const GameContext = createContext();

// Custom hook for accessing game context
export const useGame = () => {
  return useContext(GameContext);
};

// Game provider component
export const GameProvider = ({ children }) => {
  // Player state
  const [playerId, setPlayerId] = useState(localStorage.getItem('playerId') || null);
  const [playerName, setPlayerName] = useState(localStorage.getItem('playerName') || '');
  
  // Lobby state
  const [lobbyCode, setLobbyCode] = useState(localStorage.getItem('lobbyCode') || '');
  const [lobby, setLobby] = useState(null);
  const [lobbyError, setLobbyError] = useState(null);
  const [isHost, setIsHost] = useState(false);
  
  // Game state
  const [currentPlayer, setCurrentPlayer] = useState(null);
  const [currentCard, setCurrentCard] = useState(null);
  const [isMyTurn, setIsMyTurn] = useState(false);
  const [gamePhase, setGamePhase] = useState('WAITING'); // WAITING, NIGHT, DISCUSSION, VOTING, RESULTS

  // Handle player authentication
  useEffect(() => {
    if (playerId) {
      localStorage.setItem('playerId', playerId);
    } else {
      localStorage.removeItem('playerId');
    }
  }, [playerId]);

  useEffect(() => {
    if (playerName) {
      localStorage.setItem('playerName', playerName);
    } else {
      localStorage.removeItem('playerName');
    }
  }, [playerName]);

  // Subscribe to lobby updates when lobby code changes
  useEffect(() => {
    let unsubscribe = null;
    
    if (lobbyCode) {
      localStorage.setItem('lobbyCode', lobbyCode);
      setLobbyError(null);
      
      unsubscribe = subscribeLobby(lobbyCode, (lobbyData) => {
        if (lobbyData) {
          setLobby(lobbyData);
          
          // Check if current player is host
          if (lobbyData.hostId === playerId) {
            setIsHost(true);
          } else {
            setIsHost(false);
          }
          
          // Update current player data
          const player = lobbyData.players.find(p => p.id === playerId);
          if (player) {
            setCurrentPlayer(player);
            setCurrentCard(player.card);
          }
          
          // Check if it's this player's turn
          if (lobbyData.turnManager && lobbyData.turnManager.currentTurn === playerId) {
            setIsMyTurn(true);
          } else {
            setIsMyTurn(false);
          }
          
          // Determine game phase
          const currentTurn = lobbyData.turnManager?.currentTurn;
          if (!lobbyData.gameStarted) {
            setGamePhase('WAITING');
          } else if (currentTurn === 'CARD_REVEAL') {
            setGamePhase('CARD_REVEAL');
          } else if (currentTurn === 'DISCUSSION') {
            setGamePhase('DISCUSSION');
          } else if (currentTurn === 'VOTING') {
            setGamePhase('VOTING');
          } else if (currentTurn === 'ROUND_RESULT') {
            setGamePhase('ROUND_RESULT');
          } else if (currentTurn === 'GAME_OVER') {
            setGamePhase('RESULTS');
          } else {
            setGamePhase('NIGHT');
          }
        } else {
          // Lobby doesn't exist or was deleted
          setLobbyError('Lobi bulunamadı');
          setLobby(null);
        }
      });
    } else {
      localStorage.removeItem('lobbyCode');
      setLobby(null);
    }
    
    // Cleanup subscription
    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [lobbyCode, playerId]);

  // Logout/leave game function
  const leaveGame = () => {
    setPlayerId(null);
    setPlayerName('');
    setLobbyCode('');
    setLobby(null);
    setCurrentPlayer(null);
    setCurrentCard(null);
    setGamePhase('WAITING');
    
    // Clear local storage
    localStorage.removeItem('playerId');
    localStorage.removeItem('playerName');
    localStorage.removeItem('lobbyCode');
  };

  // Value to provide through context
  const value = {
    playerId,
    setPlayerId,
    playerName,
    setPlayerName,
    lobbyCode,
    setLobbyCode,
    lobby,
    lobbyError,
    isHost,
    currentPlayer,
    currentCard,
    isMyTurn,
    gamePhase,
    leaveGame
  };

  return (
    <GameContext.Provider value={value}>
      {children}
    </GameContext.Provider>
  );
};
